import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database'

export const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || `https://${location.host}/api`
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env.local file.')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseKey)

// supabaseUrl points at the /api rewrite (vercel.json) rather than at Supabase. That
// proxy is deliberate, but Vercel answers any request body over 4.5MB with a 413
// FUNCTION_PAYLOAD_TOO_LARGE, so a large attachment never reaches Storage — whose own
// limit is 50MB. This client talks to Supabase directly and is used *only* to send the
// bytes of a large upload (see utils/attachmentUpload); everything else stays proxied.
// The anon key is a JWT carrying the project ref, so the direct host can be derived
// rather than configured — one less env var to forget in a new environment. A modern
// publishable key (sb_publishable_…) has no ref: set VITE_SUPABASE_DIRECT_URL if you
// migrate to one. Falling back to supabaseUrl just restores the proxied behaviour.
function directUrlFromKey(key: string): string | null {
  try {
    const payload = key.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const ref = JSON.parse(json).ref
    return typeof ref === 'string' && ref ? `https://${ref}.supabase.co` : null
  } catch {
    return null
  }
}

export const supabaseDirectUrl =
  import.meta.env.VITE_SUPABASE_DIRECT_URL || directUrlFromKey(supabaseKey) || supabaseUrl

// Those uploads are authorised by a signed-upload token rather than by the session, so
// this client never needs auth storage — two GoTrueClients would fight over the same
// localStorage keys.
export const supabaseDirect =
  supabaseDirectUrl === supabaseUrl
    ? supabase
    : createClient<Database>(supabaseDirectUrl, supabaseKey, {
        auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
      })

export default supabase